import React, { useState } from 'react'
import { App, Button, Col, Form, Input, Row, Typography } from 'antd'
import { useIntl } from 'react-intl'

import IntlMessages from '../../util/IntlMessages'
import FormattedDate from '@vacationtracker/shared/components/formatted-date'
import Api from '@vacationtracker/shared/services/api'

import { IPaymentInfoResponseDiscount } from '@vacationtracker/shared/types/payment-information'

interface IBillingCouponCode {
  discounts?: IPaymentInfoResponseDiscount[]
  onCouponApplied?: () => void
  showApplyButton?: boolean
}

const { Paragraph } = Typography

const BillingCouponCode = ({
  discounts = [],
  onCouponApplied,
  showApplyButton = false,
}: IBillingCouponCode): React.ReactElement => {
  const { notification } = App.useApp()
  const { formatMessage } = useIntl()
  const [promoCode, setPromoCode] = useState('')
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false)

  const handleApplyCoupon = async () => {
    if (!promoCode.trim()) return

    setIsApplyingCoupon(true)
    try {
      await Api.post('/stripe/coupon-code', { promoCode: promoCode.trim() })
      notification.success({
        message: formatMessage({ id: 'components.billingCouponCode.couponApplied' }),
      })
      setPromoCode('')
      onCouponApplied?.()
    } catch (error) {
      notification.error({ message: error.response?.data?.message || formatMessage({ id: 'error.somethingWentWrong' })})
    } finally {
      setIsApplyingCoupon(false)
    }
  }

  const renderDiscount = (discount: IPaymentInfoResponseDiscount, index: number) => (
    <Paragraph key={index}>
      <IntlMessages
        id="components.billingCouponCode.activeCouponInfoSimple"
        values={{
          name: discount.name || <IntlMessages id="billing.couponDeleted" />,
          start: <FormattedDate value={new Date(discount.start * 1000).toISOString()} format="MMMM Do YYYY." />,
        }}
      />
    </Paragraph>
  )

  return (
    <>
      {discounts.length > 0 && (
        <>
          <Paragraph><IntlMessages id="components.billingCouponCode.activeCouponInfoTitle" values={{ total: discounts.length }} />:</Paragraph>
          {discounts.map((discount, index) => renderDiscount(discount, index))}
        </>
      )}
      <Form.Item
        wrapperCol={{ span: 24 }}
        labelCol={{ md: { span: 24}, sm: { span: 24 }}}
        labelAlign="left"
        name='promoCode'
        label={<IntlMessages id="components.billingCouponCode.couponCode" />}
      >
        <Row gutter={8}>
          <Col xxl={6} xl={8} lg={10} md={12} sm={{ span: 24 }} xs={{ span: 24 }}>
            <Input
              size='large'
              value={promoCode}
              name='promoCode'
              onChange={e => setPromoCode(e.target.value)}
            />
          </Col>
          {showApplyButton && <Col xxl={18} xl={16} lg={14} md={12} sm={24}>
            <Button
              size='large'
              type="primary"
              loading={isApplyingCoupon}
              disabled={isApplyingCoupon || !promoCode.trim()}
              onClick={handleApplyCoupon}
            >
              <IntlMessages id="components.billingCouponCode.applyCode" />
            </Button>
          </Col>}
        </Row>
      </Form.Item>
    </>
  )


}

export default BillingCouponCode